import { intersection } from "es-toolkit";

import { getDesignerNodeDataByIdCreate, getNodeSlotIdCreate } from "@skilitics/nautilus-core-designer-data";

import type { GetManifestByTypeId } from "../../../types/index.ts";
import type { NodeActionInterceptor } from "../types.ts";

interface Options {
  getManifest: GetManifestByTypeId;
  beforeRemove?: (nodeId: number) => void;
}

// this is the implementation of the onRemove hook on the designer manifest's children
export const childNodeRemovalInterceptorCreate = ({ getManifest, beforeRemove }: Options): NodeActionInterceptor => ({
  // needs to be before because we need the removed not not to be removed so we can find out where it is
  beforeAction: (action, { getState, dispatch }) => {
    if (action.type !== "REMOVE") return;

    const state = getState();
    const getNodeSlotId = getNodeSlotIdCreate(state);
    const nodeId = action.payload.id;

    const slotId = getNodeSlotId(nodeId);
    if (!slotId) return;

    beforeRemove?.(nodeId);

    const parentTypeId = state.designsById[slotId.parentId]?.typeId;
    const { designer } = getManifest(parentTypeId);
    const entry = designer.children?.[slotId.name]?.removable;
    if (!entry || entry === true || !("onRemove" in entry) || !entry.onRemove) return;

    const onRemove = entry.onRemove;
    const getById = getDesignerNodeDataByIdCreate(state);
    const node = getById(nodeId);
    const parentNode = getById(slotId.parentId);
    onRemove(
      {
        ...parentNode,
        setConfig: (cfgPart) => {
          // check no multilingual properties as not supported
          const partialConfigProperties = Object.keys(cfgPart);
          const multilingualProperties = Object.keys(designer.multilingual ?? {});

          if (intersection(partialConfigProperties, multilingualProperties).length > 0) {
            throw new Error(
              `Cannot set multilingual properties in the onRemove callback for ${parentNode.typeId} slot ${slotId.name}.`,
            );
          }

          dispatch({
            type: "UPDATE_CONFIG",
            payload: { id: parentNode.id, config: { ...parentNode.config, ...cfgPart } },
          });
        },
        removeChild: (child) => dispatch({ type: "REMOVE", payload: { id: child.id } }),
      },
      node,
    );
  },
});
